
import api from './api';
import { AircraftType, Airstrip } from '../types';
import { INITIAL_AIRCRAFT, INITIAL_AIRSTRIPS } from '../constants';

/**
 * FLEET REPOSITORY SERVICE
 * Handles high-integrity communication with the Fastify Fleet Node.
 */
export const fleetApi = {
  /**
   * Fetches all registered airframes from the PostgreSQL node.
   */
  getAircrafts: async (): Promise<AircraftType[]> => {
    try {
      const response = await api.get('/fleet/aircrafts');
      // Backend now returns array directly, not wrapped in { data }
      return Array.isArray(response.data) ? response.data : response.data?.data || [];
    } catch (e) {
      console.warn("Fleet Uplink Jitter: Reverting to local cache.", e);
      return INITIAL_AIRCRAFT;
    }
  },

  /**
   * Atomic update of an aircraft profile.
   */
  updateAircraft: async (id: string, updates: Partial<AircraftType>): Promise<AircraftType> => {
    try {
      const response = await api.patch(`/fleet/aircrafts/${id}`, updates);
      // Backend returns aircraft directly, not wrapped in { data }
      return response.data?.data || response.data;
    } catch (e) {
      console.error("Update Rejected by Node:", e);
      throw e; // Re-throw to let component handle error
    }
  },

  /**
   * Commissions a new airframe into the flight registry.
   */
  createAircraft: async (aircraft: Partial<AircraftType>): Promise<AircraftType> => {
    try {
      const response = await api.post('/fleet/aircrafts', aircraft);
      // Backend returns aircraft directly, not wrapped in { data }
      return response.data?.data || response.data;
    } catch (e) {
      console.error("Create Aircraft Failed:", e);
      throw e; // Re-throw to let component handle error
    }
  },

  /**
   * Decommissions an airframe from active service.
   */
  deleteAircraft: async (id: string): Promise<void> => {
    await api.delete(`/fleet/aircrafts/${id}`);
  },

  /**
   * Retrieves the master list of airstrips and ground stations.
   */
  getAirstrips: async (): Promise<Airstrip[]> => {
    try {
      const response = await api.get('/fleet/airstrips');
      // Backend returns { success, data, count }, extract data
      return Array.isArray(response.data) ? response.data : response.data?.data || [];
    } catch (e) {
      console.warn("Airstrips Uplink Jitter: fallback to rates registry.", e);
      try {
        const fallback = await api.get('/rates/airstrips');
        return Array.isArray(fallback.data) ? fallback.data : fallback.data?.data || [];
      } catch (inner) {
        console.warn("Rates registry fallback failed. Reverting to local cache.", inner);
        return INITIAL_AIRSTRIPS;
      }
    }
  },

  /**
   * Updates commercial and navigation fees for a specific station.
   */
  updateAirstrip: async (code: string, updates: Partial<Airstrip>): Promise<Airstrip> => {
    try {
      const response = await api.patch(`/fleet/airstrips/${code}`, updates);
      // Backend returns { success, data }, extract data
      return response.data?.data || response.data;
    } catch (e) {
      console.error("Update Airstrip Failed:", e);
      throw e; // Re-throw to let component handle error
    }
  }
};
